// Helpers around /api/get_filer_companies/.
//
// A ComPass company officer is promoted to Filer at login (see
// helpers/compass.js). That account is not an enrolled filer of the company,
// so the endpoint can only find its companies through the eSECURE identity —
// hence the esecure_id / keycloak_id params.

const isUsable = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  value !== "null" &&
  value !== "undefined";

// The signed-in eSECURE identity, as stored at login. Either key may be
// missing — a plain eFAST account has neither.
export const getEsecureIdentity = () => {
  const esecureId = localStorage.getItem("esecure_id");
  const keycloakId = localStorage.getItem("keycloak_id");

  return {
    esecure_id: isUsable(esecureId) ? esecureId.trim() : null,
    keycloak_id: isUsable(keycloakId) ? keycloakId.trim() : null,
  };
};

// Stores the eSECURE identity of the account being signed in. Keys with no
// value are removed so a previous session's identity can never leak into the
// next one.
export const setEsecureIdentity = ({ esecureId, keycloakId }) => {
  [
    ["esecure_id", esecureId],
    ["keycloak_id", keycloakId],
  ].forEach(([key, value]) => {
    if (isUsable(value)) {
      localStorage.setItem(key, value.trim());
    } else {
      localStorage.removeItem(key);
    }
  });
};

// Builds the /api/get_filer_companies/ URL for the signed-in user.
export const filerCompaniesUrl = ({ email, distinct = false } = {}) => {
  const identity = getEsecureIdentity();
  const params = new URLSearchParams();

  params.append("email", email || localStorage.getItem("user_email") || "");
  if (identity.esecure_id) params.append("esecure_id", identity.esecure_id);
  if (identity.keycloak_id) params.append("keycloak_id", identity.keycloak_id);
  if (distinct) params.append("distinct", "true");

  return `/api/get_filer_companies/?${params.toString()}`;
};

// True when the row belongs to an account that is originally a company user —
// a ComPass officer promoted to Filer, not an enrolled filer of the company.
export const isCompanyUserCompany = (company) =>
  !!company && company.company_user === true;

// Uploading needs a registered company that is cleared to receive forms.
// `is_registered` is only sent for company-user rows; older rows omit it.
export const canUploadForCompany = (company) => {
  if (!company) return false;
  if (company.is_registered === false) return false;

  return company.can_upload === true;
};
